import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';

const QUALIFICATIONS = [
  'Certificate in Graphic Design','Certificate in Information Technology','Certificate in Film Studies',
  'Diploma in Graphic Design','Diploma in Information Technology','Diploma in Film Production',
  'Advanced Diploma in Multimedia Design','Bachelor of Arts in Film Studies',
  'Bachelor of Science in Information Technology','Bachelor of Design (Graphic Design)',
  'Postgraduate Diploma in Education','Master of Arts in Film & Media',
  'Master of Science in Computer Science','PGCE (Post Graduate Certificate in Education)',
];

const SPECIALISATIONS = [
  'Cinematography','Video Editing','Film Directing','Screenwriting','Advanced Film Production',
  'Documentary Filmmaking','Motion Graphics','Animation','Graphic Design','UI/UX Design',
  'Branding & Identity','Digital Illustration','Print Design','Photography','Multimedia Design',
  'Web Development','Software Engineering','Networking & Systems','Cybersecurity',
  'Database Administration','Mobile App Development','Digital Marketing','Cloud Computing',
];

const ROLES = ['admin', 'teacher', 'student', 'parent'];
const roleColor = { admin: '#D32F2F', teacher: '#1976D2', student: '#388E3C', parent: '#F57C00' };

const emptyForm = {
  firstName: '', lastName: '', email: '', password: '', role: 'student', active: true,
  // student fields
  grade: '', gender: '', dob: '', phone: '', address: '',
  // teacher fields
  specialization: '', qualification: '', joinDate: '', employeeNumber: '',
  // parent fields
  occupation: '', relationshipLabel: 'Guardian',
};

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [isModalOpen, setModal]   = useState(false);
  const [current, setCurrent]     = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const loadUsers = async () => {
    try {
      setLoading(true); setError('');
      const data = await apiService.getAll('users');
      setUsers(data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const filtered = useMemo(() =>
    users.filter(u => {
      const matchSearch = [u.firstName, u.lastName, u.email, u.role].join(' ').toLowerCase().includes(search.toLowerCase());
      const matchRole   = roleFilter ? u.role === roleFilter : true;
      return matchSearch && matchRole;
    }), [search, roleFilter, users]);

  const openAdd = () => { setCurrent(null); setForm(emptyForm); setModal(true); };

  const openEdit = async user => {
    setCurrent(user);
    // Load role-specific profile data
    let extra = {};
      if (user.role === 'student') {
        try {
          const s = await apiService.get(`students/user/${user._id}`);
          extra = {
            grade: s.grade || '', gender: s.gender || '', dob: s.dob || '',
            phone: s.phone || '', address: s.address || '',
          };
        } catch (_) {}
      } else if (user.role === 'teacher') {
        try {
          const t = await apiService.get(`teachers/user/${user._id}`);
          extra = {
            specialization: t.specialization || '', qualification: t.qualification || '',
            joinDate: t.joinDate || '', employeeNumber: t.employeeNumber || '',
            phone: t.phone || '', address: t.address || '',
          };
        } catch (_) {}
      } else if (user.role === 'parent') {
        extra = { phone: user.phone || '', address: user.address || '', occupation: user.occupation || '', relationshipLabel: user.relationshipLabel || 'Guardian' };
      }

    setForm({
      ...emptyForm,
      firstName: user.firstName || '', lastName: user.lastName || '',
      email: user.email || '', password: '', role: user.role || 'student',
      active: user.active ?? true, ...extra,
    });
    setModal(true);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const buildPayload = () => {
    const payload = {
      firstName: form.firstName, lastName: form.lastName,
      email: form.email, role: form.role, active: form.active,
    };
    if (form.password) payload.password = form.password;

    if (form.role === 'student') {
      payload.studentData = { grade: form.grade, gender: form.gender, dob: form.dob, phone: form.phone, address: form.address };
    } else if (form.role === 'teacher') {
      payload.teacherData = { specialization: form.specialization, qualification: form.qualification, joinDate: form.joinDate, employeeNumber: form.employeeNumber, phone: form.phone, address: form.address };
    } else if (form.role === 'parent') {
      payload.phone = form.phone; payload.address = form.address;
      payload.occupation = form.occupation; payload.relationshipLabel = form.relationshipLabel;
    }
    return payload;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError('');
      const payload = buildPayload();
      if (current) {
        await apiService.update('users', current._id, payload);
      } else {
        if (!form.password) { setError('Password is required for new users.'); setSaving(false); return; }
        await apiService.create('users', payload);
      }
      setModal(false); setForm(emptyForm); await loadUsers();
      toast(current ? 'User updated successfully.' : 'User created successfully.', 'success');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user.');
      toast(err.response?.data?.message || 'Failed to save user.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try { await apiService.delete('users', id); await loadUsers(); toast('User deleted successfully.', 'success'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to delete user.'); toast(err.response?.data?.message || 'Failed to delete user.', 'error'); }
  };

  if (loading) return <LoadingSpinner message="Loading users..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>User Management</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-container" style={{ display: 'flex', gap: '1rem' }}>
        <input type="text" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ minWidth: 140 }}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="no-data-message">No users found.</td></tr>
            ) : filtered.map(user => (
              <tr key={user._id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  <span style={{ background: `${roleColor[user.role] || '#666'}22`, color: roleColor[user.role] || '#666', padding: '0.2rem 0.6rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600 }}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span style={{ background: user.active ? '#d4edda' : '#f8d7da', color: user.active ? '#155724' : '#721c24', padding: '0.2rem 0.5rem', borderRadius: 8, fontSize: '0.82rem' }}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" onClick={() => openEdit(user)}>Edit</button>
                    <button className="btn-icon" onClick={() => handleDelete(user._id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title={current ? `Edit User — ${current.firstName} ${current.lastName}` : 'Add User'}>
        <form onSubmit={handleSubmit} className="form">
          {/* Basic info */}
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>{current ? 'New Password (leave blank to keep)' : 'Password'}</label>
              <input type="password" name="password" value={form.password} onChange={handleChange} required={!current} minLength={6} placeholder={current ? 'Leave blank to keep current' : ''} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <select name="role" value={form.role} onChange={handleChange} disabled={!!current}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
              <input type="checkbox" name="active" id="u-active" checked={form.active} onChange={handleChange} />
              <label htmlFor="u-active" style={{ marginBottom: 0 }}>Active</label>
            </div>
          </div>

          {/* Student fields */}
          {form.role === 'student' && (
            <>
              <hr style={{ margin: '0.75rem 0', opacity: 0.3 }} />
              <div className="form-row">
                <div className="form-group">
                  <label>Year Level</label>
                  <select name="grade" value={form.grade} onChange={handleChange} required>
                    <option value="">Select Year Level</option>
                    <option value="Year 1">Year 1</option>
                    <option value="Year 2">Year 2</option>
                    <option value="Year 3">Year 3</option>
                    <option value="Year 4">Year 4</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={form.gender} onChange={handleChange} required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input type="date" name="dob" value={form.dob} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} />
              </div>
            </>
          )}

          {/* Teacher fields */}
          {form.role === 'teacher' && (
            <>
              <hr style={{ margin: '0.75rem 0', opacity: 0.3 }} />
              <div className="form-row">
                <div className="form-group">
                  <label>Specialization</label>
                  <SearchableSelect name="specialization" value={form.specialization} onChange={handleChange} options={SPECIALISATIONS} placeholder="Search specialisation..." />
                </div>
                <div className="form-group">
                  <label>Qualification</label>
                  <SearchableSelect name="qualification" value={form.qualification} onChange={handleChange} options={QUALIFICATIONS} placeholder="Search qualification..." />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Employee Number</label>
                  <input name="employeeNumber" value={form.employeeNumber} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Join Date</label>
                  <input type="date" name="joinDate" value={form.joinDate} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input name="address" value={form.address} onChange={handleChange} />
                </div>
              </div>
            </>
          )}

          {/* Parent fields */}
          {form.role === 'parent' && (
            <>
              <hr style={{ margin: '0.75rem 0', opacity: 0.3 }} />
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Occupation</label>
                  <input name="occupation" value={form.occupation} onChange={handleChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Relationship</label>
                  <select name="relationshipLabel" value={form.relationshipLabel} onChange={handleChange}>
                    <option value="Guardian">Guardian</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input name="address" value={form.address} onChange={handleChange} />
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update User' : 'Add User'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagement;
